import React, { useState, useRef, useEffect } from 'react';
import { Linkedin, Github, Twitter, Users, Star, MapPin } from 'lucide-react';
import { teamData } from '../data/mock';

export const Team = () => {
  const [hoveredMember, setHoveredMember] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="team" ref={sectionRef} className="py-20 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-32 right-16 w-32 h-32 rounded-full opacity-5 animate-float" style={{
          background: `radial-gradient(circle, var(--accent-primary), transparent)`,
          animationDelay: '2s'
        }}></div>
        <div className="absolute bottom-40 left-20 w-28 h-28 rounded-full opacity-10 animate-float" style={{
          background: `radial-gradient(circle, var(--accent-purple), transparent)`,
          animationDelay: '4s'
        }}></div>
        
        {/* Connected dots pattern */}
        <div className="absolute top-20 left-1/4 w-2 h-2 rounded-full animate-float opacity-40" style={{
          background: 'var(--accent-primary)',
          animationDelay: '1s'
        }}></div>
        <div className="absolute top-32 left-1/2 w-1 h-1 rounded-full animate-float opacity-60" style={{
          background: 'var(--accent-purple)',
          animationDelay: '3s'
        }}></div>
        <div className="absolute bottom-32 right-1/3 w-3 h-3 rounded-full animate-float opacity-30" style={{
          background: 'var(--accent-primary)',
          animationDelay: '2s'
        }}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full mb-6" style={{
            background: 'var(--accent-bg)',
            border: '1px solid var(--accent-primary)'
          }}>
            <Users className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
            <span className="body-sm font-medium" style={{ color: 'var(--accent-primary)' }}>
              Meet Our Team
            </span>
          </div>
          <h2 className="display-md mb-6" style={{ color: 'var(--text-primary)' }}>
            The Minds Behind the Magic
          </h2>
          <p className="body-lg max-w-3xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            Our diverse team of automation experts, engineers, and visionaries work tirelessly 
            to deliver innovative solutions that transform businesses worldwide.
          </p>
        </div>

        {/* Team Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {teamData.map((member, index) => {
            const isHovered = hoveredMember === index;
            
            return (
              <div
                key={member.id}
                className={`group relative transition-all duration-1000 delay-${index * 100} ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                onMouseEnter={() => setHoveredMember(index)}
                onMouseLeave={() => setHoveredMember(null)}
              >
                {/* Team Card */}
                <div className={`p-6 rounded-2xl transition-all duration-300 ${
                  isHovered ? 'scale-105' : ''
                }`} style={{
                  background: 'var(--bg-secondary)',
                  border: `1px solid ${isHovered ? 'var(--accent-primary)' : 'var(--border-subtle)'}`
                }}>
                  {/* Profile Image Placeholder */}
                  <div className="relative mb-6">
                    <div className={`w-24 h-24 rounded-2xl mx-auto flex items-center justify-center transition-all duration-300 ${
                      isHovered ? 'animate-pulse-glow' : ''
                    }`} style={{
                      background: isHovered ? 'var(--accent-primary)' : 'var(--bg-tertiary)'
                    }}>
                      <Users className="w-12 h-12" style={{ 
                        color: isHovered ? 'var(--bg-primary)' : 'var(--accent-primary)' 
                      }} />
                    </div>
                    
                    {/* Online Status Indicator */}
                    <div className="absolute bottom-2 right-1/2 transform translate-x-6">
                      <div className="w-4 h-4 rounded-full border-2 border-white" style={{
                        background: 'var(--accent-primary)'
                      }}></div>
                    </div>
                  </div>

                  {/* Member Info */}
                  <div className="text-center mb-4">
                    <h3 className="h4 mb-1 group-hover:text-white transition-colors duration-300" 
                        style={{ color: 'var(--text-primary)' }}>
                      {member.name}
                    </h3>
                    <p className="body-sm mb-3" style={{ color: 'var(--accent-primary)' }}>
                      {member.role}
                    </p>
                    <p className="body-sm group-hover:text-gray-200 transition-colors duration-300" 
                       style={{ color: 'var(--text-secondary)' }}>
                      {member.bio}
                    </p>
                  </div>

                  {/* Social Links */}
                  <div className="flex justify-center space-x-3">
                    {member.linkedin && (
                      <a
                        href={member.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
                        style={{
                          background: 'var(--bg-tertiary)',
                          color: 'var(--text-muted)'
                        }}
                      >
                        <Linkedin className="w-4 h-4" />
                      </a>
                    )}
                    {member.github && (
                      <a
                        href={member.github}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
                        style={{
                          background: 'var(--bg-tertiary)',
                          color: 'var(--text-muted)'
                        }}
                      >
                        <Github className="w-4 h-4" />
                      </a>
                    )}
                    {member.twitter && (
                      <a
                        href={member.twitter}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110"
                        style={{
                          background: 'var(--bg-tertiary)',
                          color: 'var(--text-muted)'
                        }}
                      >
                        <Twitter className="w-4 h-4" />
                      </a>
                    )}
                  </div>

                  {/* Hover Effect Border */}
                  <div className={`absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl transition-all duration-300 ${
                    isHovered ? 'opacity-100' : 'opacity-0'
                  }`} style={{
                    background: `linear-gradient(90deg, var(--accent-primary), var(--accent-purple))`
                  }}></div>
                </div>

                {/* Floating Role Badge */}
                <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 px-3 py-1 rounded-full text-xs font-medium transition-all duration-300 ${
                  isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
                }`} style={{
                  background: 'var(--accent-primary)',
                  color: 'var(--bg-primary)'
                }}>
                  {member.role.split(' ')[0]}
                </div>
              </div>
            );
          })}
        </div>

        {/* Team Stats */}
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-6 mb-16 transition-all duration-1000 delay-600 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          {[
            { icon: Users, number: "250+", label: "Team Members" },
            { icon: MapPin, number: "15", label: "Countries" },
            { icon: Star, number: "4.9", label: "Team Rating" },
            { icon: Users, number: "98%", label: "Retention Rate" }
          ].map((stat, index) => {
            const IconComponent = stat.icon;
            return (
              <div
                key={index}
                className="group text-center p-6 rounded-2xl transition-all duration-300 hover:scale-105"
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-subtle)'
                }}
              >
                <div className="w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center group-hover:animate-pulse-glow transition-all duration-300" style={{
                  background: 'var(--bg-tertiary)'
                }}>
                  <IconComponent className="w-6 h-6" style={{ color: 'var(--accent-primary)' }} />
                </div>
                <div className="h3 mb-2 group-hover:scale-110 transition-transform duration-300" 
                     style={{ color: 'var(--accent-primary)' }}>
                  {stat.number}
                </div>
                <div className="body-sm group-hover:text-white transition-colors duration-300" 
                     style={{ color: 'var(--text-muted)' }}>
                  {stat.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* Join Team CTA */}
        <div className={`text-center transition-all duration-1000 delay-800 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="max-w-3xl mx-auto p-8 rounded-2xl relative overflow-hidden" style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)'
          }}>
            {/* Background Animation */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-0 left-0 w-16 h-16 rounded-full animate-float" style={{
                background: `radial-gradient(circle, var(--accent-primary), transparent)`,
                animationDelay: '1s'
              }}></div>
              <div className="absolute bottom-0 right-0 w-12 h-12 rounded-full animate-float" style={{
                background: `radial-gradient(circle, var(--accent-purple), transparent)`,
                animationDelay: '2s'
              }}></div>
            </div>

            <div className="relative z-10">
              <h3 className="h2 mb-4" style={{ color: 'var(--text-primary)' }}>
                Want to Join Our Team?
              </h3>
              <p className="body-lg mb-6" style={{ color: 'var(--text-secondary)' }}>
                We're always looking for talented individuals who share our passion for automation 
                and innovation. Explore career opportunities with us.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                <button 
                  className="btn-primary"
                  onClick={() => {
                    const element = document.querySelector('#contact');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                >
                  View Open Positions
                </button>
                <button className="btn-secondary">
                  Learn About Our Culture
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};